/**
 * FeedbackInput Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 5: Create FeedbackInput component
 *
 * Textarea for NeedsRefinement feedback with character limit.
 *
 * @see AC-4.2.7.3 - Feedback required for NeedsRefinement
 *
 * @module components/governance/FeedbackInput
 */

// =============================================================================
// Types
// =============================================================================

export interface FeedbackInputProps {
  /** Current feedback value */
  value: string;
  /** Callback when feedback changes */
  onChange: (value: string) => void;
  /** Maximum character limit (default: 500) */
  maxLength?: number;
  /** Whether input is disabled */
  disabled?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_MAX_LENGTH = 500;

// =============================================================================
// Component
// =============================================================================

/**
 * Feedback input for NeedsRefinement vote option
 */
export function FeedbackInput({
  value,
  onChange,
  maxLength = DEFAULT_MAX_LENGTH,
  disabled = false,
}: FeedbackInputProps) {
  const charCount = value.length;
  const isOverLimit = charCount > maxLength;
  const isEmpty = value.trim().length === 0;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // Allow typing but show error if over limit
    onChange(newValue.slice(0, maxLength));
  };

  return (
    <div className="space-y-2" data-testid="feedback-input">
      <label
        htmlFor="refinement-feedback"
        className="block text-sm font-medium text-foreground"
      >
        Feedback <span className="text-red-500">*</span>
        <span className="text-muted-foreground font-normal ml-1">
          (required for "Needs Refinement")
        </span>
      </label>

      <textarea
        id="refinement-feedback"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder="Explain what changes or clarifications are needed for this proposal..."
        rows={4}
        className={`
          w-full px-3 py-2 rounded-lg border bg-background text-foreground
          placeholder:text-muted-foreground
          focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isOverLimit ? 'border-red-500' : isEmpty ? 'border-yellow-500' : 'border-border'}
        `}
        data-testid="feedback-textarea"
        aria-describedby="feedback-char-count"
        aria-invalid={isOverLimit || isEmpty}
        aria-required="true"
      />

      <div
        id="feedback-char-count"
        className={`text-xs text-right ${isOverLimit ? 'text-red-500' : 'text-muted-foreground'}`}
        data-testid="feedback-char-count"
      >
        {charCount}/{maxLength} characters
        {isEmpty && (
          <span className="text-yellow-600 ml-2" data-testid="feedback-required-warning">
            Feedback is required
          </span>
        )}
      </div>
    </div>
  );
}

export default FeedbackInput;
